/**
 * Input type, allowed values and validation for emergency info fields, inferred from the
 * field name. Names are free text (often bilingual, e.g. "रक्त समूह (Blood Group)"),
 * so matching is by keyword. Order matters: name/relationship checks run before phone.
 */

const AGE_OPTIONS = Array.from({ length: 111 }, (_, i) => String(i));

const RULES = [
  {
    match: /blood\s*(group|type)|रक्त\s*समूह|ब्लड\s*ग्रुप/i,
    spec: {
      kind: "select",
      options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "A1+", "A1-", "A2B+", "Bombay (hh)"],
      placeholder: "Select Blood Group",
    },
  },
  {
    match: /date\s*of\s*birth|\bdob\b|जन्म\s*तिथि/i,
    spec: { kind: "date" },
  },
  {
    match: /\bage\b|उम्र|आयु/i,
    spec: { kind: "select", options: AGE_OPTIONS, placeholder: "Select Age", optionSuffix: " Years" },
  },
  {
    match: /gender|\bsex\b|लिंग/i,
    spec: { kind: "select", options: ["Male", "Female", "Other"], placeholder: "Select Gender" },
  },
  {
    match: /diabetes|मधुमेह/i,
    spec: {
      kind: "select",
      options: ["No", "Type 1", "Type 2", "Prediabetes", "Gestational", "Yes (Type Unknown)"],
      placeholder: "Select",
    },
  },
  {
    match: /high\s*bp|blood\s*pressure|hypertension|रक्तचाप/i,
    spec: { kind: "select", options: ["No", "Yes", "Yes, On Medication"], placeholder: "Select" },
  },
  {
    match: /relation|रिश्ता|संबंध/i,
    spec: {
      kind: "text",
      suggestions: [
        "Father", "Mother", "Husband", "Wife", "Son", "Daughter", "Brother", "Sister",
        "Guardian", "Friend", "Relative", "Neighbour", "Colleague",
      ],
      placeholder: "e.g. Father",
      autoComplete: "off",
    },
  },
  {
    match: /id\s*type|आईडी\s*प्रकार/i,
    spec: {
      kind: "text",
      suggestions: ["Aadhaar", "PAN", "Driving Licence", "Passport", "Voter ID"],
      placeholder: "e.g. Driving Licence",
      autoComplete: "off",
    },
  },
  {
    match: /aadha?a?r|आधार/i,
    spec: { kind: "digits", length: 4, placeholder: "Last 4 Digits", inputMode: "numeric" },
  },
  {
    // Any "name" field (contact name, doctor name, full name) is plain text, even if it also says "contact"
    match: /name|नाम/i,
    spec: { kind: "text", placeholder: "Full Name", autoComplete: "name" },
  },
  {
    match: /e-?mail|ईमेल/i,
    spec: { kind: "email", placeholder: "name@example.com" },
  },
  {
    match: /phone|mobile|contact|फोन|फ़ोन|मोबाइल|संपर्क/i,
    spec: { kind: "phone", placeholder: "10-Digit Mobile Number" },
  },
  {
    // "पता" must stand alone: it also appears inside "अस्पताल" (hospital)
    match: /address|(^|[\s(])पता($|[\s)])/i,
    spec: { kind: "textarea", placeholder: "House, Street, City, PIN" },
  },
  {
    match: /medication|medicine|दवा|condition|स्थिति|allerg|एलर्जी|other\s*information|अन्य|notes?/i,
    spec: { kind: "textarea", placeholder: "Separate multiple items with commas" },
  },
];

const DEFAULT_SPEC = { kind: "text", placeholder: "Value", autoComplete: "off" };

/** Sentinel value when the owner opts out of sharing an essential field. Counts as filled. */
export const PREFER_NOT_TO_SHARE = "Prefer not to share";

export function isPreferNotToShare(value = "") {
  const v = String(value || "").trim().toLowerCase();
  return (
    v === PREFER_NOT_TO_SHARE.toLowerCase() ||
    v === "don't want to share" ||
    v === "do not want to share" ||
    v === "साझा नहीं करना चाहते"
  );
}

/** The free section: available on every plan, holds only these mandatory fields. */
export const ESSENTIAL_CATEGORY = "essential";

/** Fixed fields of the essential section, in display order (mirrors backend config/pricing.js). */
export const ESSENTIAL_FIELDS = [
  { id: "age", key: "उम्र (Age)", match: /\bage\b|उम्र|आयु/i, label: { en: "Age", hi: "उम्र" } },
  { id: "gender", key: "लिंग (Gender)", match: /gender|\bsex\b|लिंग/i, label: { en: "Gender", hi: "लिंग" } },
  {
    id: "address",
    key: "पता (Address)",
    match: /address|(^|[\s(])पता($|[\s)])/i,
    label: { en: "Address", hi: "पता" },
  },
  {
    id: "bloodGroup",
    key: "रक्त समूह (Blood Group)",
    match: /blood\s*(group|type)|रक्त\s*समूह|ब्लड\s*ग्रुप/i,
    label: { en: "Blood Group", hi: "रक्त समूह" },
  },
  {
    id: "contactName",
    key: "आपातकालीन संपर्क नाम (Emergency Contact Name)",
    match: /contact\s*name|संपर्क\s*(का\s*)?नाम/i,
    label: { en: "Emergency Contact Name", hi: "आपातकालीन संपर्क नाम" },
  },
  {
    id: "contactPhone",
    key: "आपातकालीन संपर्क फोन (Emergency Contact Phone)",
    match: /contact\s*(phone|number|no\b|mobile)|संपर्क\s*(फोन|फ़ोन|नंबर)/i,
    label: { en: "Emergency Contact Phone", hi: "आपातकालीन संपर्क फोन" },
  },
];

export function essentialFieldId(key = "") {
  return ESSENTIAL_FIELDS.find((field) => field.match.test(key))?.id || null;
}

/**
 * Suggestions are stored bilingually as "हिंदी (English)", e.g. "आधार (अंतिम 4 अंक) (Aadhar - Last 4 digits)".
 * Returns just the part for the UI language ("en" | "hi").
 */
export function localizeFieldName(name = "", lang = "en") {
  const match = String(name).match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (!match || !/[\u0900-\u097F]/.test(match[1])) return name;
  return lang === "hi" ? match[1].trim() : match[2].trim();
}

export function getFieldSpec(key = "", { includePreferNotShare = false } = {}) {
  const rule = RULES.find((r) => r.match.test(key));
  const spec = rule ? { ...rule.spec, options: rule.spec.options ? [...rule.spec.options] : undefined } : { ...DEFAULT_SPEC };
  if (includePreferNotShare && spec.kind === "select" && Array.isArray(spec.options)) {
    if (!spec.options.some((opt) => isPreferNotToShare(opt))) {
      spec.options = [...spec.options, PREFER_NOT_TO_SHARE];
    }
  }
  return spec;
}

/** Strips formatting from phone numbers so they dial correctly (tel:+91XXXXXXXXXX). */
export function normalizeFieldValue(key, value = "") {
  const trimmed = value.trim();
  const spec = getFieldSpec(key);
  if (spec.kind === "phone") {
    let digits = trimmed.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith("0") && /^[6-9]/.test(digits.slice(1))) digits = digits.slice(1);
    return digits;
  }
  if (spec.kind === "digits") return trimmed.replace(/\D/g, "");
  return trimmed;
}

/** Returns an error message, or null when the value is acceptable for this field. */
export function validateFieldValue(key, value = "") {
  if (isPreferNotToShare(value)) return null;
  const spec = getFieldSpec(key);
  const normalized = normalizeFieldValue(key, value);
  if (!normalized) return null;
  const label = key.match(/\(([^)]+)\)\s*$/)?.[1] || key;

  switch (spec.kind) {
    case "phone":
      // Indian mobile, or landline with STD code (0 + 9–10 digits)
      if (/^[6-9]\d{9}$/.test(normalized) || /^0\d{9,10}$/.test(normalized)) return null;
      return `${label}: enter a valid 10-digit mobile number`;
    case "digits":
      return normalized.length === spec.length ? null : `${label}: enter exactly ${spec.length} digits`;
    case "email":
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) ? null : `${label}: enter a valid email address`;
    case "date": {
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return `${label}: enter a valid date`;
      if (date > new Date()) return `${label}: date cannot be in the future`;
      return null;
    }
    default:
      return null;
  }
}
